// The issues grid's "Download Report" — IA_ABSORPTION_PLAN Phase 4 step 13.4. `IssuesGrid`
// (`@rdrive/plugin-items-grid` since 2026-10-12; an app component until then) renders `@rdrive/plugin-items-grid`'s ItemsGridPaginatedReportsDialog (a dumb dialog: it collects
// the options and emits `generate`) and runs what this returns; the dialog holds no SQL, no endpoint
// and no analytics, so the report query, the rows, the PaginatedReportGenerator call (with its one
// retry), the GA event and the Group by / Order by options are here. They are this app's own
// `components/dialogs/ItemsGridPaginatedReportsDialog.vue` of HEAD (54e2705), verbatim but for:
//   - the plan-rendering branch is KEPT (Daniel, 2026-10-09: "keep the PlanRenderer"), app-side here as
//     `renderPlans` + `fetchAdditionalDataLocal`, behind the opt-in `includePlans` flag. It defaults to
//     false, exactly as the old dialog's `includeExtra` always was (the long-press checkbox that set it
//     was removed in the debt kill), so no UI path reaches it today. `utils/PlanRenderer.js` stays as it
//     was (its API key: `// TODO(CI): move to VITE_PLAN_RENDERER_API_KEY`);
//   - the retry status text (`isRetrying` / `retryMessage`) is gone — the template never showed it.
// Step 14 (IA_ABSORPTION_PLAN): a failed report alerts ONCE (`generateReport`'s catch —
// `downloadSummaryReport` only logs and rethrows; it alerted too, so every failure showed two
// identical alerts), the alert's `reportGenerationFailed` key now exists in src/locales/ (it existed
// in no bundle, so the alert read "reportGenerationFailed: …"), and the debug logs of the report
// query (the statement, the raw result, one `Mapped row:` line per issue) and the plan branch's
// two data dumps are gone (the retry path's one-line logs stay).
//
// Store-free: `useIssuesGrid.js` (called by each page — IssuesGrid called this until it
// became a package component) passes the connection, the settings, the logo, the grid's report
// where-clause (useIssuesGridData's `reportWhereClause`), the root composer's `t` and `trackEvent`,
// and hands `generateReport`/`generating`/the options to the package grid as props.
import { ref, unref } from 'vue';
import PaginatedReportGenerator, { reportTimeZone } from '@rdrive/helper-paginated-report';
import PlanRenderer from '@/utils/PlanRenderer';

const DEFAULT_LOGO_URL = 'https://paginated-report-service.rdrive.io/assets/logo.png';

// The dialog's Matrix ("Numeric") mode stays disabled, as it was in this app's own dialog
export const ISSUES_REPORT_DISABLED_MODES = ['matrix'];

/**
 * @param {object} deps
 * @param {object|import('vue').Ref} deps.conn  DuckDB connection (DuckDBService's gated `conn`)
 * @param {object|import('vue').Ref} deps.settings  the dashboard's settings (apiUrl, projectCode, projectName)
 * @param {string|import('vue').Ref} deps.logoUrl  the company logo; falsy → the report service's default
 * @param {string|import('vue').Ref<string>} deps.whereClause  the grid's search + conditions, report aliases
 * @param {(key: string) => string} deps.t  the root composer's translator
 * @param {(name: string, params?: object) => void} deps.trackEvent  GA
 * @param {object} [deps.duckdb]  the DuckDBService (the plan branch fetches issue x/y through it)
 * @param {boolean|import('vue').Ref<boolean>} [deps.includePlans=false]  after the report, render a
 *   plan per drawing with `PlanRenderer` (the old dialog's `includeExtra`; off, as it always was)
 * @returns {{ generating: import('vue').Ref<boolean>, groupByOptions: Array, orderByOptions: Array,
 *   generateReport: (options: object) => Promise<void> }}
 */
export function useIssuesGridReport({ conn, settings, logoUrl, whereClause, t, trackEvent, duckdb = null, includePlans = false }) {
    const cfg = () => unref(settings);
    const generating = ref(false);

    // The dialog's Grid-mode selects ({ title, value }) — translated once, as the old dialog did at setup
    const groupByOptions = [
        { title: t('none'), value: 'exchangeToken' },
        { title: t('issueTemplateTitle'), value: 'issueTemplateTitle' },
        { title: t('categoryTitle'), value: 'CategoryTitle' },
        { title: t('subcategoryTitle'), value: 'SubcategoryTitle' },
        { title: t('issueCategory'), value: 'issueCategory' },
        { title: t('issueTypes'), value: 'issueType' },
        { title: t('issueItems'), value: 'issueItem' },
        { title: t('locationTitle'), value: 'locationTitle' },
        { title: t('drawingTitle'), value: 'DrawingTitle' },
        { title: t('responsibleParty'), value: 'responsibleParty' },
        { title: t('assigneeTeamName'), value: 'assigneeTeamName' },
        { title: t('controllerTeamName'), value: 'controllerTeamName' },
        { title: t('creatorTeamName'), value: 'creatorTeamName' },
        { title: t('creatorUserName'), value: 'creatorUserName' },
        { title: t('statusTitle'), value: 'statusTitle' }
    ];

    const orderByOptions = [
        { title: t('issueClientId'), value: 'issueTemplateCode' },
        { title: t('issueCategory'), value: 'issueCategory' },
        { title: t('issueTypes'), value: 'issueType' },
        { title: t('issueItems'), value: 'issueItem' },
        { title: t('locationTitle'), value: 'locationTitle' },
        { title: t('drawingTitle'), value: 'DrawingTitle' },
        { title: t('responsibleParty'), value: 'responsibleParty' },
        { title: t('assigneeTeamName'), value: 'assigneeTeamName' },
        { title: t('controllerTeamName'), value: 'controllerTeamName' },
        { title: t('creatorTeamName'), value: 'creatorTeamName' },
        { title: t('creatorUserName'), value: 'creatorUserName' },
        { title: t('statusTitle'), value: 'statusTitle' },
        { title: t('issueLastUpdated'), value: 'issueLastUpdated' },
        { title: t('dueBy'), value: 'dueBy' }
    ];

    // The localized display title for a group-by value
    const groupByDisplay = (groupBy) => {
        const option = groupByOptions.find(opt => opt.value === groupBy);
        return option ? option.title : groupBy;
    };

    const getCompanyURL = (clientId) => {
        const apiUrl = cfg().apiUrl;
        return `${apiUrl}/${clientId.toLowerCase()}`;
    };

    const generateItemURL = (itemId, subItemId) => {
        const apiUrl = cfg().apiUrl;
        const projectCode = cfg().projectCode;
        return `${apiUrl}/projects/${projectCode}/items/${itemId}/sub-items/${subItemId}`;
    };

    const delay = (ms) => new Promise(resolve => setTimeout(resolve, ms));

    const isRetryableError = (error) => {
        const retryableCodes = [423, 429, 524];

        // Check if it's an HTTP error with a retryable status code
        if (error.response && error.response.status) {
            return retryableCodes.includes(error.response.status);
        }

        // Check if error message contains the status codes (for different error formats)
        if (error.message) {
            return retryableCodes.some(code => error.message.includes(code.toString()));
        }

        return false;
    };

    const downloadSummaryReport = async ({ mode, reportName, fileFormat, rows, groupBy, orderBy }) => {
        try {
            trackEvent('downloadReport', {
                action_type: 'button_click',
                apiUrl: cfg().apiUrl,
                project_code: cfg().projectCode,
                project_name: cfg().projectName,
                report_name: reportName,
                file_format: fileFormat,
                report_length: rows.length
            });

            // PaginatedReportGenerator POSTs to its own fixed endpoint (not behind the proxy)
            const reportGenerator = new PaginatedReportGenerator();

            const myLogoUrl = unref(logoUrl) ? unref(logoUrl) : DEFAULT_LOGO_URL;

            const reportData = {
                ItemsLatestData: rows,
                ProjectInfo: [
                    {
                        displayName: cfg().projectName,
                        logoURL: myLogoUrl,
                        timeZone: reportTimeZone()
                    }
                ],
                Translation: [
                    {
                        locale: "",
                        subheader: t('summaryReport'),
                        groupBy: t('groupBy'),
                        category: t('category'),
                        subcategory: t('subcategory'),
                        controller: t('controllers'),
                        drawing: t('drawings'),
                        assignee: t('assignedTo'),
                        location: t('location'),
                        item: t('item'),
                        subitem: t('subItem'),
                        total: t('total'),
                        createdDate: t('createdDate'),
                        updatedDate: t('updatedDate'),
                        status: t('status'),
                        issueClientId: t('issueClientId'),
                        photo: t('photo'),
                        responsePhoto: t('responsePhoto'),
                        issueDescription: t('issueDescription'),
                        issueItem: t('issueItem'),
                        DrawingTitle: t('drawingTitle'),
                        locationTitle: t('locationTitle'),
                        dueBy: t('dueBy'),
                        responsibleParty: t('responsibleParty'),
                        creatorTeamName: t('creatorTeamName'),
                        footerReportGen: t('reportGeneratedAt')
                    }]
            };

            const parameters = [
                {
                    key: "CompanyURLParam",
                    value: 'https://' + cfg().apiUrl
                },
                {
                    key: "ProjectParam",
                    value: cfg().projectCode
                }
            ];
            if (mode === 'grid') {
                parameters.push(
                    {
                        key: "GroupByParam",
                        value: groupBy || 'issueClientId'
                    },
                    {
                        key: "GroupByDisplayParam",
                        value: groupByDisplay(groupBy)
                    },
                    {
                        key: "OrderByParam",
                        value: orderBy || 'issueClientId'
                    }
                );
            }
            // First attempt
            try {
                await reportGenerator.generateReport({
                    reportName: reportName,
                    fileFormat: fileFormat,
                    parameters: parameters,
                    data: reportData
                });
            } catch (firstError) {
                console.log('First attempt failed:', firstError);

                // Check if it's a retryable error
                if (isRetryableError(firstError)) {
                    console.log('Retryable error detected, attempting retry...');

                    // Wait before retry (3 seconds for rate limiting, 5 seconds for server issues)
                    const waitTime = firstError.response?.status === 429 ? 3000 : 5000;
                    await delay(waitTime);

                    try {
                        // Second attempt
                        console.log('Attempting retry...');
                        await reportGenerator.generateReport({
                            reportName: reportName,
                            fileFormat: fileFormat,
                            parameters: parameters,
                            data: reportData
                        });

                        console.log('Retry successful');
                    } catch (secondError) {
                        console.error('Retry attempt also failed:', secondError);
                        throw new Error(`Report generation failed after retry. Last error: ${secondError.message}`);
                    }
                } else {
                    // Not a retryable error, throw immediately
                    throw firstError;
                }
            }

        } catch (error) {
            console.error('Error generating report:', error);
            throw error; // Rethrown: generateReport alerts it
        }
    };

    /**
     * The dialog's `generate`. Every failure is alerted; `generating` is false again when it returns.
     * @param {object} options  { mode: 'grid'|'matrix', reportType, fileFormat, pageSize ('' for the
     *   spreadsheet formats), groupBy, orderBy }
     */
    // --- plan rendering (opt-in: includePlans) — the old dialog's branch, verbatim but store-free ---

    // Fetch the issues' x/y through the proxy into `issueSearch` and join them onto the items
    const fetchAdditionalDataLocal = async (filteredItems) => {
        try {
            const c = unref(conn);
            if (!duckdb || !c) {
                console.error('DuckDB not initialized yet. Please wait for initialization to complete.');
                return filteredItems; // Return original if fetch fails
            }

            if (!filteredItems || filteredItems.length === 0) {
                console.warn('No filtered items provided for additional data fetch.');
                return filteredItems;
            }

            // Extract IDs from filteredItems
            const ids = filteredItems.map(item => item.id).filter(id => id);

            if (ids.length === 0) {
                console.warn('No valid IDs found in filtered items.');
                return filteredItems;
            }

            // Build the query string with repeated ?ids= for each ID
            const queryParams = ids.map(id => `ids=${encodeURIComponent(id)}`).join('&');
            const path = `/api/${cfg().projectCode}/issues/search?${queryParams}&fields=id%2Cx%2Cy&pageSize=9999`;
            const table = 'issueSearch';

            const progressCallback = (progress) => {
                console.log('Progress:', progress); // Simple logging for now
            };
            // Create the issueSearch table if it doesn't exist (before the insert)
            await c.query(`
            CREATE TABLE IF NOT EXISTS issueSearch (
                id VARCHAR,
                x DOUBLE,
                y DOUBLE
            );
        `);

            // The shared DuckDBService takes its options as an object
            await duckdb.downloadAndInsertJSON(path, table, { progressCallback });

            console.log(`[${new Date().toISOString()}] Additional data fetched and inserted into ${table}`);

            const result = await c.query(`
                SELECT id, x, y
                FROM issueSearch
            `);
            const additionalDataArray = result.toArray().map(row => row.toJSON());

            // Create a map for quick lookup by id, then join with filteredItems
            const additionalDataMap = new Map(additionalDataArray.map(data => [data.id, { x: data.x, y: data.y }]));
            const joinedItems = filteredItems.map(item => {
                const additional = additionalDataMap.get(item.id);
                return additional ? { ...item, x: additional.x, y: additional.y } : item;
            });

            return joinedItems;
        } catch (error) {
            console.error(`[${new Date().toISOString()}] Error fetching and joining additional data:`, error);
            return filteredItems; // Return original on error
        }
    };

    // Render one plan per drawing for the report's rows
    const renderPlans = async (rows) => {
        // Group the rows by drawingId
        const drawingsMap = new Map();
        rows.forEach(item => {
            if (!drawingsMap.has(item.DrawingId)) {
                drawingsMap.set(item.DrawingId, {
                    drawingTitle: item.drawing,
                    drawingUrl: item.drawingUrl,
                    items: []
                });
            }
            drawingsMap.get(item.DrawingId).items.push(item);
        });

        // For each unique drawing, render the plan
        for (const [DrawingId, { drawingTitle, drawingUrl, items: drawingItems }] of drawingsMap) {
            console.log(`Rendering plan for drawing: ${drawingTitle} (ID: ${DrawingId})`);

            // Fetch additional data and join x/y for this drawing's items
            const filteredItems = await fetchAdditionalDataLocal(drawingItems);

            // Generate locations dynamically from unique locations in filteredItems
            const locations = [...new Map(filteredItems.map(item => [item.issueLocationId, {
                id: item.issueLocationId,
                x: item.locationX ?? 100,
                y: item.locationY ?? 100,
                drawingId: item.DrawingId,
                title: item.locationTitle
            }])).values()];

            // Generate issues dynamically from filteredItems
            const issues = filteredItems.map((item, index) => ({
                id: item.id || `ISS${String(index + 1).padStart(3, '0')}`,
                x: item.x ?? 400, // Use joined x if available, else default
                y: item.y ?? 577, // Use joined y if available, else default
                drawingId: item.DrawingId,
                issueTemplateCode: item.issueCategory || 'MCI',
                number: parseInt(item.issueClientId) || 37883,
                createdDate: item.issueLastUpdated || '2025-01-03T04:28:00.000Z',
                receivedDate: item.dueBy || '2025-01-03T09:26:00.000Z',
                customPositioning: true,
                icon: 'safety-icon',
                label: item.issueClientId,
                issueStatusHistories: [
                    {
                        id: 'history-1',
                        issueId: item.id,
                        issueStatusId: item.statusTitle || 'status-1',
                        color: item.statusColor || '#FF0000',
                        title: item.statusTitle || 'Open',
                        isInitial: true,
                        isTerminal: false,
                        isPassed: false,
                        createdDate: item.issueLastUpdated || '2025-01-03T04:28:00.000Z'
                    }
                ]
            }));

            const apiClient = null; // Placeholder, as in the old dialog
            const renderer = new PlanRenderer(apiClient);

            await renderer.render({
                DrawingId,
                drawingUrl,
                drawingTitle,
                locations,
                issues,
                outputFormat: 'JPG'
            });

            console.log(`Plan rendering completed for drawing: ${drawingTitle}`);
        }

        console.log('All plan renderings initiated successfully');
    };

    const generateReport = async ({ mode, reportType, fileFormat, pageSize, groupBy, orderBy }) => {
        generating.value = true;
        let reportName;

        // Dynamic "today" date for test values (ISO format)
        const todayISO = new Date().toISOString();
        const where = unref(whereClause);

        const itemsQuery = `SELECT
                        i.id,
                        i.issueTemplateId,
                        it.title as issueTemplateTitle,
                        regexp_replace(i.issueClientId, '-[^-]*$', '') as issueTemplateCode,
                        it.categoryTitle as CategoryTitle,
                        it.subcategoryTitle as SubcategoryTitle,
                        it.type as templateType,
                        i.issueClientId,
                        i.issueClientId as clientId,
                        CAST(TRY_CAST(regexp_extract(i.issueClientId, '-([^-]*)$', 1) AS BIGINT) AS VARCHAR) as clientIdNumber,
                        u.displayName as issueClientName,
                        u.email as issueClientEmail,
                        i.issueLocationId,
                        l.title as locationTitle,
                        l.title as location,
                        l.x as locationX,
                        l.y as locationY,
                        l.drawingId,
                        l.drawingId as DrawingId,
                        l.drawingTitle,
                        l.drawingTitle as drawingTitle,
                        l.drawingTitle as drawing,
                        ld.url as drawingUrl,
                        i.issueAssignee,
                        t_assignee.name as assigneeTeamName,
                        t_assignee.name as assigneeTeam,
                        t_assignee.code as assigneeTeamCode,
                        i.issueControlledByTeamId,
                        t_controller.name as controllerTeamName,
                        t_controller.name as controlledByTeam,
                        t_controller.code as controllerTeamCode,
                        i.issueCreatedByTeamId,
                        t_creator.name as creatorTeamName,
                        i.issueCreatedByUserId,
                        u_creator.displayName as creatorUserName,
                        i.issueStatusId,
                        st.title as statusTitle,
                        st.color as statusColor,
                        st.title as status,
                        st.color as color,
                        st.isDraft,
                        st.isInitial,
                        st.isTerminal,
                        st.isPassed,
                        i.issueLastUpdated,
                        i.issueType,
                        i.issueCategory,
                        i.issueItem,
                        i.responsibleParty,
                        i.photo,
                        i.priority,
                        i.dueBy,
                        i.responsePhoto,
                        i.issueDescription,
                        '${todayISO}' as closedDate,
                        CAST(CAST(sh_created.itemCreatedDate AS DATE) AS VARCHAR) as createdDate,
                        '${todayISO}' as statusCreatedDate,
                        '${todayISO}' as assigneeCreatedDate,
                        'test' as subItemId,
                        'test' as template,
                        'test' as category,
                        'test' as subCategory,
                        'test' as sort,
                        'test' as type,
                        'test' as subItem,
                        'test' as profile,
                        '' as x,
                        '' as y
                    FROM filteredIssues i
                    LEFT JOIN item_templates it ON i.issueTemplateId = it.id
                    LEFT JOIN users u ON i.issueClientId = u.id
                    LEFT JOIN locations l ON i.issueLocationId = l.id
                    LEFT JOIN drawings ld ON l.drawingId = ld.id
                    LEFT JOIN teams t_assignee ON i.issueAssignee = t_assignee.id
                    LEFT JOIN teams t_controller ON i.issueControlledByTeamId = t_controller.id
                    LEFT JOIN teams t_creator ON i.issueCreatedByTeamId = t_creator.id
                    LEFT JOIN users u_creator ON i.issueCreatedByUserId = u_creator.id
                    LEFT JOIN status_templates st ON i.issueStatusId = st.id
                    LEFT JOIN (
                        SELECT SubItemId, MIN(CreatedDate) as itemCreatedDate
                        FROM status_history
                        GROUP BY SubItemId
                    ) sh_created ON i.id = sh_created.SubItemId
${where ? where + ' AND i.issueClientId IS NOT NULL' : 'WHERE i.issueClientId IS NOT NULL'}
                `;

        const timestamp = new Date().toISOString();

        try {
            const result = await unref(conn).query(itemsQuery);

            const rows = result.toArray();
            // Log warning if item_templates data is missing, but don't block
            if (rows.length === 0 || rows.every(row => row.toJSON().issueTemplateTitle === null)) {
                console.warn(`[${timestamp}] No matching data found in item_templates table or table is empty. Report will include NULL for issueTemplateTitle. Check API endpoint /api/${cfg().projectCode}/reporting/project-item-templates for data.`);
            }

            const mappedRows = rows.map(row => {
                const jsonRow = row.toJSON();
                return {
                    ...jsonRow,
                    companyURL: getCompanyURL(jsonRow.issueClientId),
                    project: cfg().projectName,
                    exchangeToken: '',
                    url: generateItemURL(jsonRow.id, jsonRow.subItemId || jsonRow.id),
                    CONCAT: `${jsonRow.issueClientId}${jsonRow.id}`
                };
            });

            // pageSize is '' for the spreadsheet formats (the dialog shows no page size for them)
            const pageSizeSuffix = pageSize ? ` - ${pageSize}` : ' - Excel';
            if (mode === 'grid') {
                reportName = 'RDrive - Issue Report' + pageSizeSuffix;
            } else {
                reportName = `RDrive - Summary Report (Count by Form) - ${reportType}` + pageSizeSuffix;
            }

            await downloadSummaryReport({ mode, reportName, fileFormat, rows: mappedRows, groupBy, orderBy });

            // Opt-in (includePlans, default off — as the old dialog's includeExtra always was)
            if (unref(includePlans)) {
                await renderPlans(rows);
            }

        } catch (error) {
            console.error('Error in generateReport:', error);
            alert(`${t('reportGenerationFailed')}: ${error.message}`);
        } finally {
            generating.value = false;
        }
    };

    return { generating, groupByOptions, orderByOptions, generateReport };
}
